// src/pages/AdminAgendaCitas/AdminAgendaCitas.tsx
import React, { useEffect, useState, useCallback, useRef } from 'react';
import './AdminAgendaCitas.scss';
import { useApiAppointments } from '../../api/api.appoiments';
import { useAppSelector } from '../../contexts/hooks';
import { SysI_Agenda, SysI_AgendaCitas, EstadoCita, AppointmentData, AppointmentFormData } from '../../types/types.agendaCitas';
import CalendarPanel from './Components/CalendarPanel';
import AppointmentScheduler from './Components/AppointmentScheduler';
import AppointmentForm from './Components/AppointmentForm';
import { parseTimeString, calculateEndTime } from './Utils/parseTime';
import { newToast } from '../../components';
import LoadIndicator from 'devextreme-react/load-indicator';

const AgendaCitasIndex = () => {
  // Estados compartidos entre componentes
  const [agendas, setAgendas] = useState<SysI_Agenda[]>([]);
  const [citas, setCitas] = useState<SysI_AgendaCitas[]>([]);
  const [loadingAgendas, setLoadingAgendas] = useState(false);
  const [loadingCitas, setLoadingCitas] = useState(false);
  const [loadingAppointmentForm, setLoadingAppointmentForm] = useState(false);
  const [loadingPatientSearch, setLoadingPatientSearch] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedAgendaId, setSelectedAgendaId] = useState<number | null>(null);
  const [startDayHour, setStartDayHour] = useState(8);
  const [endDayHour, setEndDayHour] = useState(18);
  const [selectedAgendaIds, setSelectedAgendaIds] = useState<number[]>([]);
  const schedulerRef = useRef<any>(null);
  // Estado para el rango visible del scheduler
  const [visibleRange, setVisibleRange] = useState<{ start: Date; end: Date } | null>(null);
  // Estados para el formulario
  const [appointmentFormVisible, setAppointmentFormVisible] = useState(false);
  const [appointmentFormData, setAppointmentFormData] = useState<AppointmentFormData>({
    id: null, // ID de la cita, null si es nueva
    agendaId: null,
    patientId: undefined,
    patientDocument: '',
    patientName: '',
    patientLastName: '',
    patientGender: null,
    patientBirthDate: null,
    startDate: new Date(),
    endDate: new Date(),
    notes: '',
    status: EstadoCita.Programada,
    usuario: '',
    quotationId: null,
    preorderId: null,
  });
  const [isEditMode, setIsEditMode] = useState(false);
  const [appointmentId, setAppointmentId] = useState<number | null>(null);
  const [isSearchingPatient, setIsSearchingPatient] = useState(false);
  const [patientFound, setPatientFound] = useState(false);
  const Us_Id = useAppSelector((x) => x.userInfo.User?.Us_Id);
  const Prm_Citas_R_Todas = useAppSelector((x) => x.userInfo.Permissions?.Prm_Citas_R_Todas);
  const Prm_Citas_C = useAppSelector((x) => x.userInfo.Permissions?.Prm_Citas_C);
  const Prm_Citas_U = useAppSelector((x) => x.userInfo.Permissions?.Prm_Citas_U);

  // API
  const { getAgendas, getCitasByAgenda, createCita, updateCita } = useApiAppointments();

  // Cargar agendas al inicio
  useEffect(() => {
    loadAgendas();
  }, []);

  const loadAgendas = async () => {
    setLoadingAgendas(true);
    try {
      const agendasData = await getAgendas();
      // Filtrar agendas según permisos
      let filteredAgendas = agendasData;
      if (Prm_Citas_R_Todas) {
        filteredAgendas = agendasData.filter((agenda) => agenda.Ag_Estado === true);
      } else if (Us_Id) {
        filteredAgendas = agendasData.filter((agenda) => agenda.Ag_Us_Id === Us_Id && agenda.Ag_Estado === true);
      } else {
        filteredAgendas = [];
      }
      setAgendas(filteredAgendas);
      if (filteredAgendas.length > 0) {
        setSelectedAgendaIds(filteredAgendas.map((agenda) => agenda.Ag_Id));
        setSelectedAgendaId(agendasData[0].Ag_Id);
      } else {
        setSelectedAgendaIds([]);
        setSelectedAgendaId(null);
      }
    } catch (error) {
      console.error('Error al cargar agendas:', error);
    } finally {
      setLoadingAgendas(false);
    }
  };

  // Cargar citas cuando cambia la selección de agendas
  useEffect(() => {
    if (selectedAgendaIds.length > 0) {
      const Inicio = schedulerRef.current?.instance.getStartViewDate() || new Date();
      const Fin = schedulerRef.current?.instance.getEndViewDate() || new Date();
      fetchData(Inicio, Fin);      
      updateSchedulerHours();
    } else {
      setCitas([]);
    }
  }, [selectedAgendaIds]);

  // useEffect para disparar fetchData cuando cambia el rango visible o las agendas
  useEffect(() => {
    if (visibleRange && selectedAgendaIds.length > 0) {
      fetchData(visibleRange.start, visibleRange.end);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleRange, selectedAgendaIds]);


  

  // Handler para cambios de vista o fecha en el Scheduler
  const handleViewOrDateChange = (start: Date, end: Date) => {    
    setVisibleRange({ start, end });
  };

  // fetchData recibe el rango visible
  const fetchData = async (Inicio: Date|string, Fin: Date|string) => {
    setLoadingCitas(true);
    try {
      const LstAgendas = selectedAgendaIds;
      const dataToSend = {
        Inicio,
        Fin,
        LstAgendas,
      };           
      const citasPromises = await getCitasByAgenda(dataToSend);      
      setCitas(citasPromises);      
      
    } catch (error) {
      console.error('📡 Error al cargar citas:', error);
      setCitas([]);
    } finally {
      setLoadingCitas(false);
    }
  };

  // La función original se mantiene para compatibilidad
  const loadCitasByAgenda = async (agendaId: number) => {
    try {
      //const citasData = await getCitasByAgenda(agendaId);
      //setCitas(citasData);
    } catch (error) {
      console.error('Error al cargar citas:', error);
    }
  };

  // Actualizar horarios del scheduler basado en la agenda
  const updateSchedulerHours = () => {
    if (selectedAgendaIds.length === 0) return;

    // Encontrar la hora de inicio más temprana y la hora de fin más tardía
    let earliestStartHour = 23; // Iniciar con un valor alto
    let latestEndHour = 0; // Iniciar con un valor bajo

    // Iterar por todas las agendas seleccionadas
    selectedAgendaIds.forEach((agendaId) => {
      const agenda = agendas.find((a) => a.Ag_Id === agendaId);
      if (!agenda || !agenda.Ag_HoraInicio || !agenda.Ag_HoraFin) return;

      // Parsear horas
      const startTime = parseTimeString(agenda.Ag_HoraInicio);
      const endTime = parseTimeString(agenda.Ag_HoraFin);

      // Actualizar valores si encontramos una hora de inicio más temprana
      if (startTime.hour < earliestStartHour) {
        earliestStartHour = startTime.hour;
      }

      // Actualizar valores si encontramos una hora de fin más tardía
      const calculatedEndHour = endTime.hour + (endTime.minute > 0 ? 1 : 0);
      if (calculatedEndHour > latestEndHour) {
        latestEndHour = calculatedEndHour;
      }
    });

    // Establecer valores predeterminados si no se pudieron determinar
    setStartDayHour(earliestStartHour !== 23 ? earliestStartHour : 8);
    setEndDayHour(latestEndHour !== 0 ? latestEndHour : 18);
  };

  // Función para obtener la duración de cita
  const getSelectedAgendaPeriod = () => {
    if (!selectedAgendaId) return 30;

    const agenda = agendas.find((a) => a.Ag_Id === selectedAgendaId);
    return agenda?.Ag_PeriodoCita || 30;
  };

  // Obtener color según el estado
  const getColorByStatus = (status: number) => {
    const colorMap = {
      1: '#3788d8', // Programada
      2: '#28a745', // Completada
      3: '#dc3545', // Cancelada
      4: '#6c757d', // No Asistió
    };
    return colorMap[status as keyof typeof colorMap] || '#3788d8';
  };

  // Preparar citas para el scheduler
  const appointmentsData: AppointmentData[] = citas.map((cita) => {
    const startDate = new Date(cita.Ct_FechaAtencion);
    const endDate = new Date(cita.Ct_FechaFinAtencion);

    // Obtener la agenda asociada a la cita
    const agenda = agendas.find((a) => a.Ag_Id === cita.Ct_Ag_Id);

    // Determinar el color basado en estado y agenda
    let color = getColorByStatus(cita.Ct_Estado);

    // Si la cita está programada (estado 1) y la agenda tiene un color definido, usar el color de la agenda
    if (cita.Ct_Estado === EstadoCita.Programada && agenda?.Ag_Color) {
      color = agenda.Ag_Color;
    }

    // Crear un título más descriptivo con el nombre del paciente
    const title = cita.Paciente ? `${cita.Paciente.Pc_Nombre} ${cita.Paciente.Pc_Apellido1}` : `Paciente ID: ${cita.Ct_Pa_Id}`;
    const usuario = cita.Agenda ? `${cita.Agenda.Ag_Nombre}` : `Agenda ID: ${cita.Ct_Ag_Id}`;

    return {
      id: cita.Ct_Id,
      documento: cita.Paciente?.Pc_Identificacion || '',
      title: title,
      startDate: startDate,
      endDate: endDate,
      description: cita.Ct_Notas || '',
      status: cita.Ct_Estado,
      allDay: false,
      color: color,
      usuario: usuario,
      quotationId: cita.Ct_Cz_Id || null,
      preorderId: cita.Ct_POrd_Id || null,
    };
  });

  // Funciones para el formulario de citas
  const handleCreateAppointment = () => {    
    // Validar permiso para crear citas
    if (!Prm_Citas_C) {
      newToast('WARNING', 'No tienes permisos para crear citas', 3);
      return;
    }
    const now = new Date();

    // Si la fecha actual ya ha pasado de la media hora, redondear a la siguiente media hora
    const minutes = now.getMinutes();
    const roundToNext = minutes >= 30 ? 60 - minutes : 30 - minutes;

    // Crear la fecha redondeada
    const roundedDate = new Date(now);
    roundedDate.setMinutes(now.getMinutes() + roundToNext);
    roundedDate.setSeconds(0);
    roundedDate.setMilliseconds(0);

    const endDate = calculateEndTime(roundedDate, getSelectedAgendaPeriod());

    // Resetear completamente el formulario
    setAppointmentFormData({
      id: null,
      agendaId: selectedAgendaIds.length > 0 ? selectedAgendaIds[0] : null,
      patientId: undefined,
      patientDocument: '',
      patientName: '',
      patientLastName: '',
      patientGender: null,
      patientBirthDate: null,
      startDate: roundedDate,
      endDate: endDate,
      notes: '',
      status: EstadoCita.Programada,
      usuario: '',
      quotationId: null,
      preorderId: null,
    });

    setIsEditMode(false);
    setAppointmentId(null);
    setPatientFound(false);
    setAppointmentFormVisible(true);
  };

  const handleAppointmentFormOpening = async (appointmentData: any, isNew: boolean) => {
    // Mostrar loading inmediatamente para feedback visual
    setLoadingAppointmentForm(true);
    
    // Abrir modal primero para mostrar estado de carga
    setAppointmentFormVisible(true);
    
    try {
      if (isNew) {      
        // Validar permiso para crear citas
        if (!Prm_Citas_C) {
          newToast('WARNING', 'No tienes permisos para crear citas', 3);
          setLoadingAppointmentForm(false);
          setAppointmentFormVisible(false);
          return;
        }
        const startDate = appointmentData.startDate;
        const now = new Date();

        // Verificar si la fecha es anterior a la actual para nuevas citas
        if (startDate < now) {
          // Mostrar mensaje y no abrir el formulario
          newToast('WARNING', 'No se puede programar una cita en una fecha y hora pasada', 3);
          setLoadingAppointmentForm(false);
          setAppointmentFormVisible(false);
          return; // Salir de la función sin abrir el formulario
        }

        const endDate = calculateEndTime(startDate, getSelectedAgendaPeriod());      
        
        // Usar setTimeout para hacer la operación asíncrona y permitir que el UI se actualice
        await new Promise(resolve => setTimeout(resolve, 0));
        
        // Resetear completamente el formulario
        setAppointmentFormData({
          id: null,
          agendaId: selectedAgendaId,
          patientId: undefined,
          patientDocument: '',
          patientName: '',
          patientLastName: '',
          patientGender: null,
          patientBirthDate: null,
          startDate: startDate,
          endDate: endDate,
          notes: '',
          status: EstadoCita.Programada,
          usuario: '',
          quotationId: null,
          preorderId: null,
        });

        setIsEditMode(false);
        setAppointmentId(null);
        setPatientFound(false);
      } else {      
        // Validar permiso para actualizar citas
        if (!Prm_Citas_U) {
          newToast('WARNING', 'No tienes permisos para modificar citas', 3);
          setLoadingAppointmentForm(false);
          setAppointmentFormVisible(false);
          return;
        }

        // Cargar datos para edición de forma asíncrona
        await new Promise(resolve => setTimeout(resolve, 0));
        
        const cita = citas.find((c) => c.Ct_Id === appointmentData.id);
        console.log(cita)
        if (cita) {
          // Llenar datos del formulario con la cita existente
          setAppointmentFormData({
            id: cita.Ct_Id,
            agendaId: cita.Ct_Ag_Id,
            patientId: cita.Ct_Pa_Id,
            patientDocument: cita.Paciente?.Pc_Identificacion || '',
            patientName: cita.Paciente?.Pc_Nombre || '',
            patientLastName: cita.Paciente?.Pc_Apellido1 || '',
            patientGender: cita.Paciente?.Pc_Gn_Id || null,
            patientBirthDate: cita.Paciente?.Pc_FechaNacimiento ? new Date(cita.Paciente.Pc_FechaNacimiento) : null,
            startDate: new Date(cita.Ct_FechaAtencion),
            endDate: new Date(cita.Ct_FechaFinAtencion),
            notes: cita.Ct_Notas || '',
            status: cita.Ct_Estado,
            usuario: cita.Agenda.Ag_Nombre,
            quotationId: cita.Ct_Cz_Id || null,
            preorderId: cita.Ct_POrd_Id || null,
          });

          setIsEditMode(true);
          setAppointmentId(cita.Ct_Id);
          setPatientFound(true);
        }
      }
    } finally {
      setLoadingAppointmentForm(false);
    }
  };

  const handleAppointmentUpdated = useCallback(
    async (appointmentId: number, updates: Partial<AppointmentFormData>) => {
      try {
        // Actualizar el estado local de citas
        setCitas((prevCitas) =>
          prevCitas.map((cita) => {
            if (cita.Ct_Id === appointmentId) {
              // Crear una copia actualizada de la cita
              const updatedCita = { ...cita };

              // Aplicar las actualizaciones
              if (updates.preorderId !== undefined) {
                updatedCita.Ct_POrd_Id = updates.preorderId;
              }
              if (updates.quotationId !== undefined) {
                updatedCita.Ct_Cz_Id = updates.quotationId;
              }

              return updatedCita;
            }
            return cita;
          })
        );
      } catch (error) {
        console.error('Error actualizando cita localmente:', error);
        // En caso de error, recargar todas las citas
        if (visibleRange && selectedAgendaIds.length > 0) {
          await fetchData(visibleRange.start, visibleRange.end);
        }
      }
    },
    [selectedAgendaIds]
  );

  const handleSaveAppointment = async (formData: AppointmentFormData) => {
    try {
      // Validar permisos según el modo
      if (isEditMode && !Prm_Citas_U) {
        newToast('WARNING', 'No tienes permisos para modificar citas', 3);
        return;
      } else if (!isEditMode && !Prm_Citas_C) {
        newToast('WARNING', 'No tienes permisos para crear citas', 3);
        return;
      }
      
      // Normalizar la fecha para evitar problemas de zona horaria
      const normalizedStartDate = new Date(formData.startDate.getFullYear(), formData.startDate.getMonth(), formData.startDate.getDate(), formData.startDate.getHours(), formData.startDate.getMinutes());

      // Normalizar la fecha para evitar problemas de zona horaria
      const normalizedEndate = new Date(formData.endDate.getFullYear(), formData.endDate.getMonth(), formData.endDate.getDate(), formData.endDate.getHours(), formData.endDate.getMinutes());

      // Preparar datos para API usando la fecha normalizada
      const citaData: Partial<SysI_AgendaCitas> = {
        Ct_Ag_Id: formData.agendaId as number,
        Ct_Pa_Id: formData.patientId || 0,
        Ct_Cz_Id: formData.quotationId || null,
        Ct_POrd_Id: formData.preorderId || null,
        Ct_Notas: formData.notes,
        Ct_FechaAtencion: normalizedStartDate,
        Ct_FechaFinAtencion: normalizedEndate,
        Ct_Estado: formData.status,
      };

      let result;

      if (isEditMode && appointmentId) {
        citaData.Ct_Id = appointmentId;
        result = await updateCita(citaData as SysI_AgendaCitas);
      } else {
        result = await createCita(citaData as SysI_AgendaCitas);
      }

      if (result) {
        // Cargar citas de todas las agendas seleccionadas después de guardar
        if (visibleRange && selectedAgendaIds.length > 0) {
          await fetchData(visibleRange.start, visibleRange.end);
        }
        
        setAppointmentFormVisible(false);
        //newToast('SUCCESS', isEditMode ? 'Cita actualizada correctamente' : 'Cita creada correctamente', 3);
      }
      
    } catch (error) {
      console.error('Error al guardar cita:', error);
      newToast('ERROR', 'Error al guardar la cita', 3);
    }
  };

  // Manejador para cambiar la fecha desde el calendario usando el ref del Scheduler
  const handleDateChange = (date: Date) => {
    // Corrige la fecha a hora Colombia si es necesario
    // const systemDateStr = ConvertLocalDate(date);
    // const systemDate = new Date(systemDateStr);
    // Si quieres usar la corrección, descomenta las dos líneas de arriba y usa systemDate en vez de date abajo

    if (schedulerRef.current && schedulerRef.current.instance) {
      // Cambia la vista a 'day' y la fecha seleccionada
      schedulerRef.current.instance.option('currentDate', date);
      schedulerRef.current.instance.option('currentView', 'day');
    }
  };

  // Handler para drag & drop o edición directa en el scheduler
  const handleSchedulerAppointmentUpdated = async (e: any) => {
    // e.appointmentData tiene los datos nuevos
    // e.oldData tiene los datos anteriores
    try {
      const updated = e.appointmentData;
      // Solo permitir si tiene permiso de actualizar
      if (!Prm_Citas_U) {
        newToast('WARNING', 'No tienes permisos para modificar citas', 3);
        e.cancel = true;
        return;
      }
      // Normalizar fechas
      const normalizedStartDate = new Date(updated.startDate);
      const normalizedEndDate = new Date(updated.endDate);
      // Buscar la cita original para obtener los campos faltantes
      const citaOriginal = citas.find((c) => c.Ct_Id === updated.id);
      const emptyPaciente = { Pc_Id: 0, Pc_Identificacion: '', Pc_Nombre: '', Pc_Apellido1: '', Pc_Gn_Id: null, Pc_FechaNacimiento: null };
      const emptyAgenda = { Ag_Id: 0, Ag_Us_Id: 0, Ag_Sd_Id: 0, Ag_Nombre: '', Ag_Descripcion: null, Ag_Color: '', Ag_Estado: true, Ag_HoraInicio: null, Ag_HoraFin: null, Ag_PeriodoCita: null };
      const citaData = {
        Ct_Id: updated.id,
        Ct_Ag_Id: citaOriginal ? citaOriginal.Ct_Ag_Id : (updated.agendaId || selectedAgendaId || 0), // Preservar el agendaId original
        Ct_Pa_Id: updated.patientId || (citaOriginal ? citaOriginal.Ct_Pa_Id : 0),
        Ct_Cz_Id: updated.quotationId || (citaOriginal ? citaOriginal.Ct_Cz_Id : null),
        Ct_POrd_Id: updated.preorderId || (citaOriginal ? citaOriginal.Ct_POrd_Id : null),
        Ct_Notas: updated.description || (citaOriginal ? citaOriginal.Ct_Notas : ''),
        Ct_FechaAtencion: normalizedStartDate,
        Ct_FechaFinAtencion: normalizedEndDate,
        Ct_FechaSolicitud: citaOriginal ? citaOriginal.Ct_FechaSolicitud : new Date(),
        Ct_Estado: updated.status || (citaOriginal ? citaOriginal.Ct_Estado : 1),
        Paciente: citaOriginal ? citaOriginal.Paciente : emptyPaciente,
        Agenda: citaOriginal ? citaOriginal.Agenda : emptyAgenda,
      };
      const result = await updateCita(citaData);
      if (result) {        
        if (visibleRange && selectedAgendaIds.length > 0) {
          await fetchData(visibleRange.start, visibleRange.end);
        }
      }
    } catch (error) {
      console.error('Error al actualizar cita vía drag & drop:', error);
      newToast('ERROR', 'Error al actualizar la cita', 3);
      if (visibleRange && selectedAgendaIds.length > 0) {
        await fetchData(visibleRange.start, visibleRange.end);
      }
    }
  };

  return (
    <div className='scheduler-page'>
      <div className='scheduler-container'>
        {/* Panel lateral izquierdo con calendario y lista de agendas */}
        <div className='left-panel'>
          {loadingAgendas ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <LoadIndicator visible={true} />
              <span style={{ marginLeft: 12 }}>Cargando agendas...</span>
            </div>
          ) : (
            <CalendarPanel currentDate={currentDate} onDateChange={handleDateChange} agendas={agendas} selectedAgendaIds={selectedAgendaIds} onAgendaSelectionChange={setSelectedAgendaIds} onCreateAppointment={handleCreateAppointment} canCreateAppointment={Prm_Citas_C} />
          )}
        </div>

        {/* Contenido principal con el scheduler */}
        <div className='main-content' style={{ position: 'relative', minHeight: "70%" }}>
          <AppointmentScheduler
            ref={schedulerRef}
            appointments={appointmentsData}
            currentDate={currentDate}
            startDayHour={startDayHour}
            endDayHour={endDayHour}
            cellDuration={getSelectedAgendaPeriod()}
            onAppointmentFormOpening={handleAppointmentFormOpening}
            onViewOrDateChange={handleViewOrDateChange}
            onAppointmentUpdated={handleSchedulerAppointmentUpdated}
          />
        </div>

        {/* Componente del formulario */}
        <AppointmentForm 
          visible={appointmentFormVisible} 
          formData={appointmentFormData} 
          setFormData={setAppointmentFormData} 
          isEditMode={isEditMode} 
          isSearchingPatient={isSearchingPatient} 
          setIsSearchingPatient={setIsSearchingPatient} 
          patientFound={patientFound} 
          setPatientFound={setPatientFound} 
          agendas={agendas} 
          loadingForm={loadingAppointmentForm}
          loadingPatientSearch={loadingPatientSearch}
          onSave={handleSaveAppointment} 
          onCancel={() => {
            setAppointmentFormVisible(false);
          }} 
          onAppointmentUpdated={handleAppointmentUpdated} 
        />
      </div>
    </div>
  );
};

export default AgendaCitasIndex;
